<template>
  <!-- DESKTOP TABLE -->
  <div
    v-if="table_data.length > 0"
    ref="wrapperEl"
    class="hidden md:block"
    :class="styles.wrapper"
    @wheel="onWheel"
  >
    <!-- HEADER PILL -->
    <div v-if="!hide_headers" :class="styles.header_pill">
      <div :class="styles.scroll_layer" :style="styles.scroll_layer_style">
        <div :class="styles.grid_inner" :style="[styles.grid_style, scrollerTransformStyle]">
          <div
            v-for="(header, i) in headers"
            :key="header.field_name"
            :class="[styles.header_cell, sortable ? 'flex items-center group cursor-pointer' : '']"
          >
            <!-- Header text -->
            <span
              :class="styles.header_text"
              @click="sortable ? handleLocalSort(header.field_name) : null"
            >
              {{ header.label }}
            </span>

            <!-- Sort button -->
            <button
              v-if="sortable"
              class="ml-2 opacity-0 transition-opacity"
              :class="
                sortField === header.field_name ? 'block opacity-100' : 'group-hover:opacity-100'
              "
              @click="handleLocalSort(header.field_name)"
            >
              <Icon
                v-if="sortAscending"
                name="ArrowUp"
                class="h-3.5 w-3.5 text-tab-icon-selected"
                :stroke-width="1.5"
              />
              <Icon
                v-if="!sortAscending"
                name="ArrowDown"
                class="h-3.5 w-3.5 text-tab-icon-selected"
                :stroke-width="1.5"
              />
            </button>

            <!-- Resize handle -->
            <div :class="styles.resize_handle" @mousedown.prevent="startResize(i, $event)" />
          </div>
        </div>
      </div>

      <!-- Actions header overlay -->
      <div
        v-if="hasActions"
        :class="styles.actions_overlay_header"
        :style="styles.actions_overlay_style"
      />
    </div>

    <!-- ROW PILLS -->
    <div
      v-for="(item, rowIndex) in paginated ? paginatedData : sorted_data"
      :key="rowIndex"
      :class="styles.row_pill"
    >
      <div :class="styles.scroll_layer" :style="styles.scroll_layer_style">
        <div
          :class="headers.length > 2 ? styles.grid_inner : 'grid grid-cols-2 w-full'"
          :style="[styles.grid_style, scrollerTransformStyle]"
        >
          <div v-for="header in headers" :key="header.field_name" :class="styles.table_cell">
            <template v-if="fields_with_custom_styles.includes(header.field_name)">
              <slot :name="header.field_name" :row="item" />
            </template>
            <template v-else>
              <span :class="styles.cell_text" class="w-full">
                {{ formatted_field_value(item[header.field_name], header, item) }}
              </span>
            </template>
          </div>
        </div>
      </div>

      <!-- Actions overlay (pinned right) -->
      <div
        v-if="hasActions"
        ref="actionsEl"
        :class="styles.actions_overlay_cell"
        :style="styles.actions_overlay_style"
      >
        <slot name="actions" :row="item" />
      </div>
    </div>

    <!-- Custom always-visible horizontal scrollbar -->
    <div
      v-if="!hide_scrollbar"
      ref="trackEl"
      :class="styles.hscroll_track"
      @mousedown="onTrackMouseDown"
    >
      <div
        :class="styles.hscroll_thumb"
        :style="thumbStyle"
        @mousedown.stop.prevent="startThumbDrag"
      />
    </div>

    <div
      v-if="paginated && table_data.length > pagination_page_size"
      class="flex flex-col items-center justify-center py-4 w-full min-w-0"
    >
      <Pagination
        v-if="paginated"
        ref="paginationEl"
        :total-items="table_data.length"
        :page-size="pagination_page_size"
        :page-num="currentPage"
        @change-page="currentPage = $event"
        class="justify-center"
      />
    </div>
  </div>

  <!-- EMPTY STATE -->
  <div v-else class="h-auto w-full">
    <div class="h-full w-full gap-2 flex flex-col relative">
      <div :class="styles.empty_state_bg_div_base" class="bg-opacity-60 dark:bg-opacity-60"></div>
      <div :class="styles.empty_state_bg_div_base" class="bg-opacity-50 dark:bg-opacity-50"></div>
      <div :class="styles.empty_state_bg_div_base" class="bg-opacity-40 dark:bg-opacity-40"></div>
      <div :class="styles.empty_state_bg_div_base" class="bg-opacity-30 dark:bg-opacity-30"></div>
      <div :class="styles.empty_state_bg_div_base" class="bg-opacity-20 dark:bg-opacity-20"></div>

      <!-- CENTERED OVERLAY -->
      <div class="absolute inset-0 flex items-center justify-center">
        <div
          class="bg-paper-0 dark:bg-primary-4-t px-10 py-5 rounded-xl flex flex-col items-center"
        >
          <div class="mb-[1.313rem]">
            <Icon
              :name="empty_icon"
              class="h-[4.375rem] w-[4.375rem] stroke-tab-icon-selected stroke-[1.25px]"
            />
          </div>
          <div class="font-inter text-base font-semibold text-strong mb-2">
            {{ empty_primary_text }}
          </div>
          <div class="font-inter font-medium text-strong">{{ empty_secondary_text }}</div>
        </div>
      </div>
    </div>
  </div>

  <!-- MOBILE CARDS -->
  <div :class="styles.mobile_wrapper">
    <div v-for="(item, idx) of sorted_data" :key="idx" :class="styles.mobile_card">
      <div class="flex flex-col">
        <div :class="styles.mobile_data_label">{{ mobile_primary_schema.label }}</div>
        <div :class="styles.mobile_primary_header">
          {{
            mobile_primary_schema
              ? formatted_field_value(
                  item[mobile_primary_schema.field_name],
                  mobile_primary_schema,
                  item
                )
              : ""
          }}
        </div>
      </div>

      <!-- DATA POINTS -->
      <div class="mt-3">
        <!-- always-visible first 3 -->
        <div
          v-for="header of mobile_data_points.slice(0, defaul_num_shown_details_mobile)"
          :key="header.field_name"
          :class="styles.mobile_data_point"
        >
          <div :class="styles.mobile_data_label">{{ header.label }}</div>
          <div :class="styles.mobile_data_value">
            {{ formatted_field_value(item[header.field_name], header, item) }}
          </div>
        </div>

        <!-- collapsible extras -->
        <Transition
          @before-enter="beforeEnter"
          @enter="enter"
          @after-enter="afterEnter"
          @before-leave="beforeLeave"
          @leave="leave"
          @after-leave="afterLeave"
        >
          <div v-show="isMobileCardOpen(idx)" class="overflow-hidden">
            <div
              v-for="header of mobile_data_points.slice(defaul_num_shown_details_mobile)"
              :key="header.field_name"
              :class="styles.mobile_data_point"
            >
              <div :class="styles.mobile_data_label">{{ header.label }}</div>
              <div :class="styles.mobile_data_value">
                {{ formatted_field_value(item[header.field_name], header, item) }}
              </div>
            </div>
          </div>
        </Transition>
      </div>

      <div class="mt-3 flex items-center gap-1">
        <Button
          v-if="mobile_data_points.length > defaul_num_shown_details_mobile"
          variant="darkpaper-primary"
          size="sm"
          type="button"
          @click.stop="toggleMobileCard(idx)"
        >
          <template #icon>
            <Icon
              name="ChevronDown"
              class="h-5.5 w-5.5 transition-transform duration-300 ease-out"
              :class="isMobileCardOpen(idx) ? 'rotate-180' : 'rotate-0'"
            />
          </template>
        </Button>
        <slot name="actions" :row="item" v-if="has_actions" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { Button, Icon, Pagination } from "@/components";
import { formatID, getLastFour } from "@/utilities";

let defaul_num_shown_details_mobile = 6;

/** ---------------- props ---------------- */
const props = defineProps({
  table_data: { type: Array, required: true },
  table_headers: { type: Array, required: true },
  mobile_primary_field: { type: String, default: "" },
  variant: { type: String, default: "primary" },
  empty_icon: { type: String, default: "Balloon" },
  empty_primary_text: { type: String, default: "No data available" },
  empty_secondary_text: { type: String, default: "No additional information" },
  fields_with_custom_styles: { type: Array, default: () => [] },
  has_actions: { type: Boolean, default: false },
  hide_headers: { type: Boolean, default: false },
  hide_scrollbar: { type: Boolean, default: false },
  paginated: { type: Boolean, default: false },
  pagination_page_size: { type: Number, default: 6 },
  sortable: { type: Boolean, default: true },
  default_sort_field: { type: String, default: null },
  default_sort_ascending: { type: Boolean, default: true },

  // --- sizing controls (optional, but solves the "too narrow when few cols" nuance) ---
  auto_size_columns: { type: Boolean, default: true },
  auto_size_mode: { type: String, default: "content+fill" }, // "content" | "fill" | "content+fill"
  min_col_px: { type: Number, default: 140 },
  default_col_px: { type: Number, default: 160 },
  max_col_px: { type: Number, default: 520 },
  column_gap_px: { type: Number, default: 24 }, // must match grid columnGap
  autosize_sample_rows: { type: Number, default: 80 },
  cell_x_padding_px: { type: Number, default: 0 }, // extra breathing room for text
});

/** ---------------- derived ---------------- */
const hasActions = computed(() => props.has_actions);
const headers = computed(() => props.table_headers);

/** ---------------- sort ---------------- */
const sortField = ref(props.default_sort_field);
const sortAscending = ref(props.default_sort_ascending);

const handleLocalSort = (field) => {
  if (sortField.value === field) {
    sortAscending.value = !sortAscending.value;
  } else {
    sortField.value = field;
    sortAscending.value = true;
  }
};

const sorted_data = computed(() => {
  if (!props.sortable || !sortField.value) return props.table_data;

  return [...props.table_data].sort((a, b) => {
    let valA = a[sortField.value];
    let valB = b[sortField.value];

    if (valA == null && valB == null) return 0;
    if (valA == null) return 1;
    if (valB == null) return -1;

    const isDate = sortField.value.toLowerCase().includes("date") || !isNaN(Date.parse(valA));
    if (isDate) {
      valA = new Date(valA).getTime();
      valB = new Date(valB).getTime();
    }

    if (typeof valA === "number" && typeof valB === "number") {
      return sortAscending.value ? valA - valB : valB - valA;
    }

    return sortAscending.value
      ? String(valA).localeCompare(String(valB))
      : String(valB).localeCompare(String(valA));
  });
});

/** ---------------- pagination ---------------- */
const currentPage = ref(1);
const pageSize = computed(() => props.pagination_page_size);
const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;

  return sorted_data.value.slice(start, end);
});

/** ---------------- mobile schema helpers ---------------- */
const mobile_primary_schema = computed(() => {
  const found = props.table_headers.find((h) => h.field_name === props.mobile_primary_field);
  return found ?? props.table_headers?.[0] ?? null;
});

const mobile_data_points = computed(() => {
  const primary = mobile_primary_schema.value?.field_name;
  if (!primary) return props.table_headers;
  return props.table_headers.filter((h) => h.field_name !== primary);
});

/** ---------------- mobile collapse per-card ---------------- */
const openMobileCards = ref(new Set()); // default collapsed
const isMobileCardOpen = (key) => openMobileCards.value.has(key);

const toggleMobileCard = (key) => {
  const next = new Set(openMobileCards.value);
  next.has(key) ? next.delete(key) : next.add(key);
  openMobileCards.value = next;
};

/** ---------------- transition animation (mobile extras) ---------------- */
const prefersReducedMotion =
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

const beforeEnter = (el) => {
  if (prefersReducedMotion) return;
  el.style.willChange = "height, opacity, transform";
  el.style.height = "0px";
  el.style.opacity = "0";
  el.style.transform = "translateY(-6px)";
};

const enter = (el) => {
  if (prefersReducedMotion) return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const h = el.scrollHeight;
      el.style.transition =
        "height 380ms cubic-bezier(0.22, 1, 0.36, 1), opacity 260ms ease, transform 320ms cubic-bezier(0.22, 1, 0.36, 1)";
      el.style.height = `${h}px`;
      el.style.opacity = "1";
      el.style.transform = "translateY(0px)";
    });
  });
};

const afterEnter = (el) => {
  if (prefersReducedMotion) return;
  el.style.height = "auto";
  el.style.transition = "";
  el.style.willChange = "";
};

const beforeLeave = (el) => {
  if (prefersReducedMotion) return;
  el.style.willChange = "height, opacity, transform";
  el.style.height = `${el.scrollHeight}px`;
  el.style.opacity = "1";
  el.style.transform = "translateY(0px)";
};

const leave = (el) => {
  if (prefersReducedMotion) return;
  requestAnimationFrame(() => {
    el.style.transition =
      "height 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 180ms ease, transform 240ms cubic-bezier(0.4, 0, 0.2, 1)";
    el.style.height = "0px";
    el.style.opacity = "0";
    el.style.transform = "translateY(-6px)";
  });
};

const afterLeave = (el) => {
  if (prefersReducedMotion) return;
  el.style.height = "";
  el.style.opacity = "";
  el.style.transform = "";
  el.style.transition = "";
  el.style.willChange = "";
};

/** ---------------- refs / state ---------------- */
let resizeObserver;

const wrapperEl = ref(null);
const trackEl = ref(null);
const actionsEl = ref(null);

const colWidths = ref([]);
const userResized = ref(new Set());

const scrollLeft = ref(0);
const viewportWidth = ref(0);
const trackInnerWidth = ref(0);

const MIN_ACTIONS_PX = 160;
const TRACK_PAD_PX = 6; // 0.375rem
const actionsWidth = ref(MIN_ACTIONS_PX);

/** ---------------- utilities ---------------- */
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

/** ---------------- grid columns ---------------- */
const gridTemplateColumns = computed(() =>
  headers.value.map((_, i) => `${colWidths.value[i] ?? props.default_col_px}px`).join(" ")
);

//console.log(gridTemplateColumns);

/** translate grid horizontally */
const scrollerTransformStyle = computed(() => ({
  transform: `translateX(-${scrollLeft.value}px)`,
}));

/** ---------------- sizing + scroll bounds ---------------- */
const totalContentWidth = computed(() => {
  const n = colWidths.value.length;
  const cols = colWidths.value.reduce((a, b) => a + b, 0);
  const gaps = n > 1 ? props.column_gap_px * (n - 1) : 0;
  return cols + gaps;
});

const viewportScrollableWidth = computed(() => {
  const outer = viewportWidth.value || 0;
  return Math.max(0, outer - (hasActions.value ? actionsWidth.value : 0));
});

const maxScroll = computed(() =>
  Math.max(0, totalContentWidth.value - viewportScrollableWidth.value)
);

/** ---------------- thumb sizing ---------------- */
const effectiveTrackWidth = computed(() => trackInnerWidth.value || viewportWidth.value || 0);

const thumbWidth = computed(() => {
  const viewport = viewportScrollableWidth.value;
  const total = totalContentWidth.value;
  const track = effectiveTrackWidth.value;
  if (track <= 0 || total <= 0 || viewport <= 0) return 0;

  const ratio = viewport / total;
  return Math.max(36, Math.floor(track * ratio));
});

const thumbLeft = computed(() => {
  if (maxScroll.value <= 0) return 0;
  const usable = Math.max(0, effectiveTrackWidth.value - thumbWidth.value - TRACK_PAD_PX * 2);
  if (usable <= 0) return 0;
  return (scrollLeft.value / maxScroll.value) * usable;
});

const thumbStyle = computed(() => ({
  width: `${thumbWidth.value}px`,
  transform: `translateX(${TRACK_PAD_PX + thumbLeft.value}px)`,
}));

/** ---------------- measure ---------------- */
const getTrackMetrics = () => {
  const el = trackEl.value;
  if (!el) return { innerWidth: 0 };

  const cs = window.getComputedStyle(el);
  const padLeft = parseFloat(cs.paddingLeft) || 0;
  const padRight = parseFloat(cs.paddingRight) || 0;
  return { innerWidth: Math.max(0, el.clientWidth - padLeft - padRight) };
};

const measureActionsWidth = () => {
  nextTick(() => {
    const el = Array.isArray(actionsEl.value) ? actionsEl.value[0] : actionsEl.value;
    if (!el) return;
    const w = Math.ceil(Math.max(el.offsetWidth, el.scrollWidth));
    actionsWidth.value = Math.max(MIN_ACTIONS_PX, w);
  });
};

const measure = async () => {
  await nextTick();

  // Wait for layout/paint to settle (common fix for "width is 0 until resize")
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      //console.log("measuring table with table data length", props.table_data.length);
      viewportWidth.value = wrapperEl.value?.clientWidth ?? 0;
      trackInnerWidth.value = getTrackMetrics().innerWidth;

      // actions width reads from DOM too, so keep it inside the finalized layout pass
      measureActionsWidth();

      // clamp after widths are real
      scrollLeft.value = clamp(scrollLeft.value, 0, maxScroll.value);
    });
  });
};

/** ---------------- base widths seed ---------------- */
const seedWidths = () => {
  colWidths.value = headers.value.map((h) => {
    //console.log(h);
    if (h?.width) {
      const px = parseInt(String(h.width).replace("px", ""), 10);
      if (Number.isFinite(px)) return px;
    }
    return props.default_col_px;
  });
  // console.log("seeded widths", colWidths.value);
};

/** ---------------- formatting ---------------- */
const formatted_field_value = (value, schema, row) => {
  // console.log("formatting value", value, "with schema", schema);
  if (!schema) return value ?? "-";

  if (schema.field_type === "boolean") return value ? "Yes" : "No";

  if (schema.field_type === "date") {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("en-US", { timeZone: "UTC" });
  }

  if (schema.field_type === "number" && schema.number_type === "currency") {
    if (value === null || value === undefined || value === "") return "-";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
  }

  if (schema.field_type === "number" && schema.number_type === "percentage") {
    if (value === null || value === undefined || value === "") return "-";
    const percent = Number(value) >= 1 ? Number(value) : Number(value) * 100;
    return `${Math.round(percent * 100) / 100}%`;
  }

  if (schema.is_object_id || schema.field_name.toLowerCase() == "record_id") {
    if (value === null || value === undefined || value === "") return "-";
    return `***${formatID(value).toUpperCase()}`;
  }

  if (schema.field_name === "status" && value === "Pending S1") {
    if (row?.pending_s1_funded) {
      return `${value} (Funded)`;
    }
    return value;
  }

  return value ?? "-";
};

/** ---------------- autosize columns (content-based + optional fill) ---------------- */
let _measureCanvas;
const getMeasurer = () => {
  if (_measureCanvas) return _measureCanvas;
  _measureCanvas = document.createElement("canvas");
  return _measureCanvas;
};

const getFontForCells = () => {
  // Try to measure using a real element font once mounted. Fallback is fine pre-mount.
  // (You can also hardcode if you want guaranteed consistency.)
  const fallback = "500 16px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial";
  const root = wrapperEl.value;
  if (!root) return fallback;

  // Find any cell element
  const el = root.querySelector?.("[data-table-cell]") || root.querySelector?.("span");
  if (!el) return fallback;

  const cs = window.getComputedStyle(el);
  return `${cs.fontStyle} ${cs.fontVariant} ${cs.fontWeight} ${cs.fontSize}/${cs.lineHeight} ${cs.fontFamily}`;
};

const measureTextPx = (text, font) => {
  const canvas = getMeasurer();
  const ctx = canvas.getContext("2d");
  ctx.font = font;
  const s = text == null ? "" : String(text);
  return Math.ceil(ctx.measureText(s).width);
};

const shouldFill = computed(
  () => props.auto_size_mode === "fill" || props.auto_size_mode === "content+fill"
);
const shouldContentSize = computed(
  () => props.auto_size_mode === "content" || props.auto_size_mode === "content+fill"
);

const autoSizeColumns = () => {
  if (typeof window === "undefined") return;
  if (!props.auto_size_columns) return;
  if (!headers.value?.length) return;

  const font = getFontForCells();
  const rows = props.table_data ?? [];
  const sample = rows.slice(0, props.autosize_sample_rows);

  // 1) content sizing (or keep existing if mode is fill-only)
  let nextWidths = headers.value.map((h, i) => {
    // user resized → keep
    if (userResized.value.has(i)) return colWidths.value[i] ?? props.default_col_px;

    // explicit header width → keep
    if (h?.width) {
      const px = parseInt(String(h.width).replace("px", ""), 10);
      if (Number.isFinite(px)) return px;
    }

    if (!shouldContentSize.value) {
      // fill-only mode: start from default
      return clamp(props.default_col_px, props.min_col_px, props.max_col_px);
    }

    let max = measureTextPx(h?.label ?? "", font);

    for (const item of sample) {
      const raw = item?.[h.field_name];
      const display = formatted_field_value(raw, h, item);
      max = Math.max(max, measureTextPx(display, font));
    }

    const HEADER_RIGHT_PADDING_PX = 24; // pr-6
    const EXTRA_BREATHING_ROOM_PX = 10;

    return clamp(
      max + HEADER_RIGHT_PADDING_PX + EXTRA_BREATHING_ROOM_PX,
      props.min_col_px,
      props.max_col_px
    );
  });

  // 2) fill leftover space if table is narrower than viewport
  const viewport = viewportScrollableWidth.value || 0;
  if (shouldFill.value && viewport > 0) {
    const n = nextWidths.length;
    const gaps = n > 1 ? props.column_gap_px * (n - 1) : 0;
    const contentTotal = nextWidths.reduce((a, b) => a + b, 0) + gaps;

    if (contentTotal < viewport) {
      const extra = viewport - contentTotal;

      // columns allowed to grow
      const growable = headers.value
        .map((h, i) => ({ h, i }))
        .filter(({ h, i }) => !userResized.value.has(i) && !h?.width);

      if (growable.length) {
        const per = extra / growable.length;

        nextWidths = nextWidths.map((w, i) => {
          const canGrow = growable.some((g) => g.i === i);
          return canGrow ? Math.min(props.max_col_px, Math.round(w + per)) : w;
        });
      }
    }
  }

  colWidths.value = nextWidths;
  measure();
};

/** ---------------- styles ---------------- */
const styles = computed(() => {
  const bg = props.variant === "secondary" ? "bg-container-bg" : "bg-bg-canvas";

  const primaryColor =
    mobile_primary_schema.value?.field_type === "number" &&
    mobile_primary_schema.value?.number_type === "currency"
      ? "text-success"
      : "text-strongest";

  let empty_state_bg_div_base_primary = "bg-paper-0 dark:bg-primary-5 rounded-[20px] h-18";
  let empty_state_bg_div_base_secondary = "bg-paper-3 dark:bg-primary-4 rounded-[20px] h-18";

  return {
    wrapper: "w-full overflow-x-hidden overscroll-x-contain",
    mobile_wrapper: "flex flex-col gap-4 md:hidden w-full",
    mobile_card: ["p-4 rounded-[20px] flex flex-col gap-[0.625rem]", bg].join(" "),
    mobile_primary_header: ["text-lg font-inter font-semibold", primaryColor].join(" "),
    mobile_data_point: "grid grid-cols-2 gap-1 mb-1",
    mobile_data_label: "font-inter text-sm text-strong font-bold",
    mobile_data_value: "text-sm font-inter text-strongest max-w-full truncate",
    empty_state_bg_div_base:
      props.variant === "secondary"
        ? empty_state_bg_div_base_secondary
        : empty_state_bg_div_base_primary,

    header_pill: ["relative w-full rounded-[30px] mb-2 overflow-hidden px-8 py-2", bg].join(" "),
    row_pill: ["relative w-full rounded-[20px] mb-2 overflow-hidden px-8 py-[1.563rem]", bg].join(
      " "
    ),

    scroll_layer: "relative overflow-hidden",
    scroll_layer_style: {
      paddingRight: hasActions.value ? `${actionsWidth.value}px` : "0px",
    },

    grid_inner: "inline-grid items-center",
    grid_style: {
      gridTemplateColumns: gridTemplateColumns.value,
      columnGap: `${props.column_gap_px}px`,
      willChange: "transform",
    },

    header_cell:
      "relative min-w-0 overflow-hidden whitespace-nowrap text-xs font-inter font-bold text-strong pr-6",
    table_cell: "min-w-0 overflow-hidden whitespace-nowrap text-base font-inter text-strongest",
    header_text: "block min-w-0 truncate font-inter",
    cell_text: "block min-w-0 truncate",

    resize_handle: "absolute top-0 right-0 h-full w-2 cursor-col-resize hover:bg-black/5",

    actions_overlay_style: { width: `${actionsWidth.value}px` },

    actions_overlay_header: [
      "absolute right-0 top-0 h-full  flex items-center justify-end pr-8",
      bg,
    ].join(" "),
    actions_overlay_cell: [
      "absolute right-0 top-0 h-full  flex items-center justify-end pr-8 min-w-max pl-4",
      bg,
    ].join(" "),

    hscroll_track: [
      "h-[1.188rem] p-[0.375rem] w-full rounded-[30px] items-center flex select-none",
      bg,
    ].join(" "),
    hscroll_thumb:
      "h-[0.438rem] mr-[0.375rem] rounded-[30px] bg-navbar-invest-bg-track cursor-grab",
  };
});

/** ---------------- wheel/trackpad horizontal scroll ---------------- */
const onWheel = (e) => {
  const absX = Math.abs(e.deltaX);
  const absY = Math.abs(e.deltaY);
  const wantsHorizontal = absX > absY || e.shiftKey;

  if (!wantsHorizontal) return;
  if (maxScroll.value <= 0) return;

  e.preventDefault();
  e.stopPropagation();

  const delta = absX > absY ? e.deltaX : e.deltaY;
  scrollLeft.value = clamp(scrollLeft.value + delta, 0, maxScroll.value);
};

/** ---------------- resizing columns ---------------- */
const resizeState = reactive({ isResizing: false, index: -1, startX: 0, startW: 0 });

const startResize = (index, e) => {
  userResized.value.add(index);

  resizeState.isResizing = true;
  resizeState.index = index;
  resizeState.startX = e.clientX;
  resizeState.startW = colWidths.value[index] ?? props.default_col_px;

  window.addEventListener("mousemove", onResizeMove);
  window.addEventListener("mouseup", stopResize);
  document.body.style.cursor = "col-resize";
  document.body.style.userSelect = "none";
};

const onResizeMove = (e) => {
  if (!resizeState.isResizing) return;
  const dx = e.clientX - resizeState.startX;
  colWidths.value[resizeState.index] = Math.max(props.min_col_px, resizeState.startW + dx);
  measure();
};

function stopResize() {
  resizeState.isResizing = false;
  resizeState.index = -1;

  window.removeEventListener("mousemove", onResizeMove);
  window.removeEventListener("mouseup", stopResize);
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
}

/** ---------------- custom scrollbar thumb dragging ---------------- */
const thumb = reactive({ dragging: false, startX: 0, startLeft: 0 });

const startThumbDrag = (e) => {
  if (maxScroll.value <= 0) return;

  thumb.dragging = true;
  thumb.startX = e.clientX;
  thumb.startLeft = thumbLeft.value;

  window.addEventListener("mousemove", onThumbMove);
  window.addEventListener("mouseup", stopThumbDrag);
  document.body.style.userSelect = "none";
  document.body.style.cursor = "grabbing";
};

const onThumbMove = (e) => {
  if (!thumb.dragging) return;

  const dx = e.clientX - thumb.startX;
  const usable = Math.max(0, effectiveTrackWidth.value - thumbWidth.value - TRACK_PAD_PX * 2);

  const nextThumbLeft = clamp(thumb.startLeft + dx, 0, usable);
  const nextScroll = usable === 0 ? 0 : (nextThumbLeft / usable) * maxScroll.value;

  scrollLeft.value = clamp(nextScroll, 0, maxScroll.value);
};

function stopThumbDrag() {
  thumb.dragging = false;
  window.removeEventListener("mousemove", onThumbMove);
  window.removeEventListener("mouseup", stopThumbDrag);
  document.body.style.userSelect = "";
  document.body.style.cursor = "";
}

/** click track to jump */
const onTrackMouseDown = (e) => {
  if (maxScroll.value <= 0 || !trackEl.value) return;

  const rect = trackEl.value.getBoundingClientRect();
  const x = e.clientX - rect.left - TRACK_PAD_PX;

  const usable = Math.max(0, effectiveTrackWidth.value - thumbWidth.value - TRACK_PAD_PX * 2);
  const centered = x - thumbWidth.value / 2;
  const nextThumbLeft = clamp(centered, 0, usable);

  const nextScroll = usable === 0 ? 0 : (nextThumbLeft / usable) * maxScroll.value;
  scrollLeft.value = clamp(nextScroll, 0, maxScroll.value);
};

const goToPage = (page) => {
  if (!props.paginated) return;
  if (page < 1) return;
  currentPage.value = page;
};

defineExpose({
  currentPage,
  goToPage,
});

/** ---------------- watchers ---------------- */
watch(
  headers,
  async () => {
    seedWidths();
    await nextTick();
    measure();
    await nextTick();
    autoSizeColumns();
  },
  { immediate: true }
);

watch(
  () => props.table_data?.length,
  async () => {
    await measure();
    autoSizeColumns();
  }
);

watch(
  () => [gridTemplateColumns.value, props.hide_scrollbar, props.has_actions, props.column_gap_px],
  () => measure(),
  { deep: true }
);

onMounted(async () => {
  await measure();

  // Observe element size changes (tabs, side drawers, fonts, layout shifts, etc.)
  resizeObserver = new ResizeObserver(() => measure());
  if (wrapperEl.value) resizeObserver.observe(wrapperEl.value);
  if (trackEl.value) resizeObserver.observe(trackEl.value);

  // Fonts can shift width after mount
  document.fonts?.ready?.then(() => measure());
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect?.();
  stopResize();
  stopThumbDrag();
});
</script>
